/**
 * @file Hook for checking protected and derived field status. Consumers: every
 * control that can be locked (`FieldUI`, `EntityRecordUI`, `ReferencedListUI`).
 * Protection reads `FormType.protected.protectedFieldIds`, the same list
 * `firestore.rules` (`noProtectedFieldsChanged`) enforces. Derive targets are
 * resolved from the derive CONFIG instead of the denormalized
 * `protected.derivedFieldIds`: that list is the rules authority and lags a save
 * by one rev-bump round-trip, and the client must lock the instant the config
 * says so. The two sets stay separate — both disable the input, but protection is
 * admin intent and a derive is a computed value.
 */
import { useMemo, useCallback } from 'react';
import { useSnapshot } from 'valtio';
import { createFormDeriveEngineConfig, resolveFormDeriveLockedFieldIds } from '#types';
import { formStore } from '../store';
import { FieldId } from '#types/System.types';

/**
 * Hook to check if fields or entity references are protected for the current form.
 * Protected fields/references cannot be modified by clients - only backend functions.
 */
export const useProtectedFields = () => {
    const snap = useSnapshot(formStore.state);
    const protectedConfig = snap.formType.data?.protected;
    const formDerives = snap.formType.data?.formDerives;
    const formDeriveRev = snap.formType.data?.formDeriveRev;
    const formTypeLoading = snap.formType.loading;

    const protectedFieldIds: ReadonlySet<FieldId> = useMemo(() =>
        new Set(protectedConfig?.protectedFieldIds ?? []),
        [protectedConfig?.protectedFieldIds]
    );

    const protectedEntityReferenceIds: ReadonlySet<string> = useMemo(() =>
        new Set(protectedConfig?.protectedEntityReferenceIds ?? []),
        [protectedConfig?.protectedEntityReferenceIds]
    );

    /**
     * Computed from the authored config, never from `protected.derivedFieldIds`:
     * the same answer, but instant rather than one trigger round-trip behind the
     * save that created the derive.
     */
    const derivedFieldIds: ReadonlySet<FieldId> = useMemo(() =>
        resolveFormDeriveLockedFieldIds(
            createFormDeriveEngineConfig({ formDerives, formDeriveRev }).entries
        ),
        [formDerives, formDeriveRev]
    );

    const isFieldProtected = useCallback((fieldId: FieldId): boolean => {
        return protectedFieldIds.has(fieldId);
    }, [protectedFieldIds]);

    const isFieldDerived = useCallback((fieldId: FieldId): boolean => {
        return derivedFieldIds.has(fieldId);
    }, [derivedFieldIds]);

    const isEntityReferenceProtected = useCallback((entityRefId: string): boolean => {
        return protectedEntityReferenceIds.has(entityRefId);
    }, [protectedEntityReferenceIds]);

    return {
        isFieldProtected,
        isFieldDerived,
        isEntityReferenceProtected,
        protectedFieldIds,
        derivedFieldIds,
        protectedEntityReferenceIds,
        loading: formTypeLoading,
    };
};
